/**
 * UI-free helpers for the Triage & QA pages — kept out of the .jsx so they can be
 * tested with plain Node (see scripts/triage.test.mjs).
 */

/** Queue tabs: `id` is what the backend's `tab` parameter expects. */
export const TRIAGE_TABS = [
  { id: 'all',      label: 'All' },
  { id: 'flagged',  label: 'Flagged' },
  { id: 'critical', label: 'Critical' },
  { id: 'coaching', label: 'Coaching queue' },
  { id: 'pending',  label: 'Pending review' },
  { id: 'resolved', label: 'Resolved' },
];

/** Time windows for the queue. `days: 0` means all time. */
export const TRIAGE_WINDOWS = [
  { days: 7,  label: 'Last 7 days' },
  { days: 30, label: 'Last 30 days' },
  { days: 90, label: 'Last 90 days' },
  { days: 0,  label: 'All time' },
];

/** Query params for GET /v1/triage/queue (and /export). Empty values are left out. */
export function queueParams({ tab = 'all', q = '', days = 7, page = 0, size = 50 } = {}) {
  const p = { tab, page, size };
  const needle = String(q || '').trim();
  if (needle) p.q = needle;
  if (days && Number(days) > 0) p.days = Number(days);
  return p;
}

/**
 * "showing 51–100 of 123 calls" — computed from the server's real totals, never
 * typed. `shown` is how many rows are on the current page.
 */
export function rangeLabel({ page = 0, size = 50, total = 0, shown = 0 } = {}, noun = 'calls') {
  const t = Number(total) || 0;
  if (t === 0 || !shown) return `0 ${noun}`;
  const from = page * size + 1;
  const to = page * size + shown;
  return `showing ${from}–${to} of ${t} ${noun}`;
}

/** Page count, never below 1 (an empty list is still "page 1 of 1"). */
export function pageCount(total, size) {
  const t = Number(total) || 0;
  const s = Math.max(1, Number(size) || 1);
  return Math.max(1, Math.ceil(t / s));
}

export const canGoPrev = (page) => Number(page) > 0;
export const canGoNext = (page, total, size) => Number(page) + 1 < pageCount(total, size);

/** "Flagged (12)" — or just the label while counts have not loaded. */
export function tabLabel(tab, counts) {
  const n = counts?.[tab.id];
  return n == null ? tab.label : `${tab.label} (${n})`;
}

/** Pill class for a row's status label. */
export function statusPillClass(status) {
  switch (status) {
    case 'CRITICAL':  return 'danger filled';
    case 'Escalated': return 'warning filled';
    case 'Flagged':   return 'warning';
    case 'Resolved':  return 'success';
    default:          return 'accent';
  }
}

/** Flag chip class: compliance / hallucination style flags are the serious ones. */
export function flagPillClass(flag) {
  return /COMPLIANCE|HALLU|CRITICAL/.test(String(flag)) ? 'danger filled' : 'warning filled';
}

/**
 * Add/remove an id from a selection, returning a NEW Set (state-safe).
 */
export function toggleId(selected, id) {
  const next = new Set(selected);
  if (next.has(id)) next.delete(id); else next.add(id);
  return next;
}

/** Select every id on the page, or clear them all when they are already all selected. */
export function toggleAll(selected, pageIds) {
  const allOn = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  return allOn ? new Set() : new Set(pageIds);
}

/** Bulk-review menu entries: [action sent to the server, label, needs a coach?]. */
export const BULK_ACTIONS = [
  { action: 'RESOLVE',  label: 'Mark as resolved' },
  { action: 'ESCALATE', label: 'Escalate for a manager’s decision' },
  { action: 'FLAG',     label: 'Flag for review' },
  { action: 'UNFLAG',   label: 'Remove manual flag' },
  { action: 'COACH',    label: 'Assign to a coach…', needsCoach: true },
  { action: 'PENDING',  label: 'Back to pending review' },
];

/** Past-tense confirmation for the toast after a bulk action. */
export function bulkDoneText(action, updated, requested) {
  const verb = {
    RESOLVE: 'resolved', ESCALATE: 'escalated', FLAG: 'flagged',
    UNFLAG: 'un-flagged', COACH: 'assigned to a coach', PENDING: 'moved back to pending',
  }[action] || 'updated';
  const skipped = requested - updated;
  const base = `${updated} call${updated === 1 ? '' : 's'} ${verb}`;
  return skipped > 0 ? `${base} (${skipped} skipped — not a real voice call)` : base;
}

/** Roles that may coach, matched against the directory's pretty role text. */
export function coachOptions(options) {
  const list = Array.isArray(options) ? options : [];
  const leaders = list.filter((o) => /manager|principal|bdc|gm|ceo|director/i.test(o.meta || ''));
  return leaders.length > 0 ? leaders : list;
}

/* ───────────────────────── Call detail ───────────────────────── */

/** Issue types — same set as the backend (annotations and coaching patterns). */
export const ISSUE_TYPES = [
  { id: 'HALLUCINATION',  label: 'Hallucination' },
  { id: 'TONE',           label: 'Tone / empathy' },
  { id: 'MISSED_BOOKING', label: 'Missed booking' },
  { id: 'COMPLIANCE',     label: 'Compliance' },
  { id: 'GOOD_MOMENT',    label: 'Good moment' },
  { id: 'OTHER',          label: 'Other' },
];

export const issueLabel = (id) => ISSUE_TYPES.find((t) => t.id === id)?.label || String(id || '');

/** Pill class for an issue type: the problems are red/amber, a good moment is green. */
export function issuePillClass(id) {
  if (id === 'GOOD_MOMENT') return 'success';
  if (id === 'HALLUCINATION' || id === 'COMPLIANCE') return 'danger filled';
  return 'warning filled';
}

const SPEAKER_RE = /^\s*(ai|assistant|bot|agent|customer|caller|client|user|cust)\s*[:\-–]\s*(.*)$/i;

/**
 * Split a transcript into lines. A speaker is only named when the line itself
 * says so ("AI: …", "Customer: …"); otherwise it is null — we never guess who
 * spoke from the line's position.
 */
export function parseTranscript(text) {
  if (!text || !String(text).trim()) return [];
  // Some transcripts arrive as one line with inline labels
  // ("Agent: … Customer: … Agent: …"). Splitting on newlines alone then put the
  // whole call under the first speaker. Break the line before each explicit,
  // capitalised label that follows a sentence end — conservative enough that
  // ordinary prose ("talk to the agent: …") is never split.
  const normalised = String(text).replace(
    /([.!?])\s+(?=(?:AI|Assistant|Bot|Agent|Customer|Caller|Client|User|Cust)\s*:)/g,
    '$1\n',
  );
  return normalised
    .split(/\n+/)
    .map((raw) => raw.trim())
    .filter(Boolean)
    .map((raw, index) => {
      const m = SPEAKER_RE.exec(raw);
      if (!m) return { index, speaker: null, text: raw };
      const who = m[1].toLowerCase();
      const speaker = ['ai', 'assistant', 'bot'].includes(who) ? 'AI'
        : who === 'agent' ? 'AGENT' : 'CUSTOMER';
      return { index, speaker, text: m[2] };
    });
}

/** Indexes of the lines containing `term` (case-insensitive). Empty term → none. */
export function searchMatches(lines, term) {
  const needle = String(term || '').trim().toLowerCase();
  if (!needle) return [];
  return (lines || []).filter((l) => l.text.toLowerCase().includes(needle)).map((l) => l.index);
}

/** 83 → "1:23" (never negative, never NaN). */
export function formatClock(seconds) {
  const s = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * An honest scorecard for a real call. Everything is derived from what we really
 * have — sentiment, the flag list, whether a transcript exists — and the card is
 * labelled "estimated"; there is no analyser behind any other criterion.
 * Rows: [label, value, max, colour, note?].
 */
export function callScorecard({ sentiment = 0, quality = 0, flags = [], hasTranscript = false } = {}) {
  const has = (name) => (flags || []).some((f) => String(f).toUpperCase().includes(name));
  const clamp10 = (n) => Math.max(0, Math.min(10, Math.round(n)));
  const sent = clamp10((sentiment + 1) * 5);
  const qual = clamp10(quality / 10);
  const tone = (n) => (n >= 7 ? 'success' : n >= 4 ? 'warning' : 'danger');
  return [
    ['Overall quality (estimated)', qual, 10, tone(qual)],
    ['Sentiment', sent, 10, sent >= 6 ? 'success' : sent >= 4 ? 'warning' : 'danger'],
    ['Transcript captured', hasTranscript ? 10 : 0, 10, hasTranscript ? 'success' : 'danger',
      hasTranscript ? null : 'NO TRANSCRIPT'],
    ['No negative-sentiment flag', has('NEGATIVE') ? 0 : 10, 10, has('NEGATIVE') ? 'danger' : 'success'],
    ['No manual flag', has('MANUAL_FLAG') ? 0 : 10, 10, has('MANUAL_FLAG') ? 'danger' : 'success'],
  ];
}

/** Total/max of a scorecard, for the "QUALITY SCORECARD · 32/50" label. */
export function scorecardTotals(rows) {
  return {
    total: rows.reduce((s, r) => s + r[1], 0),
    max: rows.reduce((s, r) => s + r[2], 0),
  };
}

/** Default callback time: tomorrow 09:00 local, as a datetime-local input value. */
export function defaultCallbackAt(now = new Date()) {
  const d = new Date(now.getTime());
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Colour for a weekly trend: fewer problems is good; more good moments is good. */
export function trendColor(tag, previous, current) {
  if (current === previous) return 'var(--text-secondary)';
  const up = current > previous;
  const good = tag === 'GOOD_MOMENT' ? up : !up;
  return good ? 'var(--success)' : 'var(--danger)';
}
